import { computed, ref, watch } from 'vue';

import { useI18n } from 'vue-i18n';

import type { Property } from '@/modules/Properties/types';

import { useAccountsService } from '@/modules/Accounts/services/AccountsService';
import {
  useAccountPropertiesQuery,
  useAssignedPropertiesQuery,
  useQueryInitialLoading,
} from '@/modules/QueryClient';
import { useAccountProperties } from '@/modules/Accounts/composables/useAccountProperties';

type PropertyInfo = { name: string; url: string };

export const allPropertyInfoById = ref<Map<string, PropertyInfo>>(
  new Map(),
);

function buildPropertyInfoEntries(properties: Property[]) {
  return properties.flatMap((property) => {
    const info = { name: property.name, url: property.site };

    return [property.property_id, property.id]
      .filter((id): id is string => Boolean(id))
      .map((id) => [id, info] as const);
  });
}

function mergePropertyInfoMaps(
  existing: Map<string, PropertyInfo>,
  properties: Property[],
): Map<string, PropertyInfo> {
  const merged = new Map(existing);
  buildPropertyInfoEntries(properties).forEach(([id, info]) => {
    merged.set(id, info);
  });
  return merged;
}

function mergePropertiesById(...lists: (Property[] | undefined)[]): Property[] {
  const byId = new Map<string, Property>();

  lists.forEach((list) => {
    (list ?? []).forEach((property) => {
      const key = property.id || property.property_id;

      if (key && !byId.has(key)) {
        byId.set(key, property);
      }
    });
  });

  return Array.from(byId.values());
}

export function clearGlobalPropertyInfoCache() {
  allPropertyInfoById.value = new Map();
}

export function usePagesProperties() {
  const { t } = useI18n();
  const accountsService = useAccountsService();
  const { accountProperties } = useAccountProperties();

  const teamMemberId = computed(() => accountsService.account?.team_member?.id);
  const assignedHotelPropertiesQuery = useAssignedPropertiesQuery(teamMemberId, 'hotel');
  const assignedCorporatePropertiesQuery = useAssignedPropertiesQuery(teamMemberId, 'corporate');
  const accountPropertiesQuery = useAccountPropertiesQuery();

  const hasPropertyRestriction = computed(
    () => Boolean(teamMemberId.value),
  );

  const properties = computed<Property[]>(() => {
    if (teamMemberId.value) {
      return mergePropertiesById(
        assignedHotelPropertiesQuery.data.value,
        assignedCorporatePropertiesQuery.data.value,
      );
    }
    return [];
  });

  const isAssignedInitialLoading = useQueryInitialLoading(
    assignedHotelPropertiesQuery.isLoading,
    assignedHotelPropertiesQuery.data,
  );
  const isAssignedCorporateInitialLoading = useQueryInitialLoading(
    assignedCorporatePropertiesQuery.isLoading,
    assignedCorporatePropertiesQuery.data,
  );

  const isPropertiesInitialLoading = computed(() => {
    if (teamMemberId.value) {
      return isAssignedInitialLoading.value || isAssignedCorporateInitialLoading.value;
    }
    return false;
  });

  watch(
    properties,
    (list) => {
      allPropertyInfoById.value = mergePropertyInfoMaps(allPropertyInfoById.value, list);
    },
    { immediate: true },
  );

  const propertyInfoById = computed(() => {
    const ownedEntries = properties.value.flatMap((property) => {
      const info = { name: property.name, url: property.site, status: property.status };

      return [property.property_id, property.id]
        .filter((id): id is string => Boolean(id))
        .map((id) => [id, info] as const);
    });

    const externalEntries = accountProperties.value.map(property => [
      property.id, { name: property.name, url: property.site, status: property.status },
    ] as const);

    return new Map([
      ...ownedEntries,
      ...(accountsService.isOwnerOrAdmin ? externalEntries : []),
    ]);
  });

  function getPropertyName(propertyIds: string[]) {
    if (!propertyIds || propertyIds.length === 0) {
      return t('No properties');
    }
    if (propertyIds.length > 1) {
      return t('{count} properties', { count: propertyIds.length });
    }

    return propertyInfoById.value.get(propertyIds[0])?.name || t('Restricted property');
  }

  function getPropertyUrl(propertyId: string) {
    return propertyInfoById.value.get(propertyId)?.url || '';
  }

  function getPropertyStatus(propertyId: string) {
    return propertyInfoById.value.get(propertyId)?.status;
  }

  async function ensurePropertyInfoForIds(
    propertyIds: string[],
    accountId?: string,
  ) {
    if (!accountId || propertyIds.length === 0) {
      return;
    }

    const getMissingIds = () => [...new Set(propertyIds)].filter(
      (id) => !propertyInfoById.value.has(id) && !allPropertyInfoById.value.has(id),
    );

    const applyAccountProperties = (data: Property[] | undefined) => {
      if (data?.length) {
        allPropertyInfoById.value = mergePropertyInfoMaps(allPropertyInfoById.value, data);
      }
    };

    applyAccountProperties(accountPropertiesQuery.data.value);
    if (getMissingIds().length === 0) {
      return;
    }

    if (!accountPropertiesQuery.isFetched.value || accountPropertiesQuery.isFetching.value) {
      const { data } = await accountPropertiesQuery.refetch();
      applyAccountProperties(data);
    }
  }

  async function ensurePropertiesLoaded() {
    if (teamMemberId.value) {
      const refetches = [];

      if (!assignedHotelPropertiesQuery.data.value?.length) {
        refetches.push(assignedHotelPropertiesQuery.refetch());
      }
      if (!assignedCorporatePropertiesQuery.data.value?.length) {
        refetches.push(assignedCorporatePropertiesQuery.refetch());
      }

      await Promise.all(refetches);
    }
  }

  return {
    properties,
    propertyInfoById,
    hasPropertyRestriction,
    isPropertiesInitialLoading,

    getPropertyName,
    getPropertyUrl,
    getPropertyStatus,
    ensurePropertiesLoaded,
    ensurePropertyInfoForIds,
  };
}
